import type { DemoState, MonthlyFinancial, Project } from "../types/demo";

const demoNow = "2026-02-15T09:00:00.000Z";

const monthSeed: MonthlyFinancial[] = [
  { month: "2025-03", revenue: 990000, costOfSales: 702000, operatingExpenses: 154000 },
  { month: "2025-04", revenue: 1021000, costOfSales: 719000, operatingExpenses: 160000 },
  { month: "2025-05", revenue: 1068000, costOfSales: 741000, operatingExpenses: 166000 },
  { month: "2025-06", revenue: 1090000, costOfSales: 755000, operatingExpenses: 172000 },
  { month: "2025-07", revenue: 1110000, costOfSales: 773000, operatingExpenses: 176000 },
  { month: "2025-08", revenue: 1145000, costOfSales: 795000, operatingExpenses: 179000 },
  { month: "2025-09", revenue: 1180000, costOfSales: 818000, operatingExpenses: 183000 },
  { month: "2025-10", revenue: 1202000, costOfSales: 841000, operatingExpenses: 186000 },
  { month: "2025-11", revenue: 1220000, costOfSales: 860000, operatingExpenses: 191000 },
  { month: "2025-12", revenue: 1265000, costOfSales: 896000, operatingExpenses: 197000 },
  { month: "2026-01", revenue: 1248000, costOfSales: 912000, operatingExpenses: 199000 },
  { month: "2026-02", revenue: 1240000, costOfSales: 928000, operatingExpenses: 201000 },
];

const projects: Project[] = [
  { id: "proj-1", name: "Riverpoint Tower", manager: "Nadia Singh", budget: 850000, actualCost: 892000, burnRisk: "high" },
  { id: "proj-2", name: "Harbor Logistics Fitout", manager: "Lucas Tran", budget: 610000, actualCost: 584000, burnRisk: "low" },
  { id: "proj-3", name: "Northline Civil Package", manager: "Nadia Singh", budget: 730000, actualCost: 772000, burnRisk: "high" },
  { id: "proj-4", name: "Central Medical Retrofit", manager: "Mia O'Connor", budget: 490000, actualCost: 498000, burnRisk: "medium" },
  { id: "proj-5", name: "Metro Depot Upgrade", manager: "Oliver James", budget: 910000, actualCost: 924000, burnRisk: "medium" },
  { id: "proj-6", name: "Sunset Apartments Stage 2", manager: "Mia O'Connor", budget: 780000, actualCost: 742000, burnRisk: "low" },
  { id: "proj-7", name: "Airport Hangar Works", manager: "Kai Morgan", budget: 660000, actualCost: 701000, burnRisk: "high" },
  { id: "proj-8", name: "Eastline Schools Bundle", manager: "Lucas Tran", budget: 1020000, actualCost: 1014000, burnRisk: "low" },
  { id: "proj-9", name: "Greenridge Utilities", manager: "Kai Morgan", budget: 540000, actualCost: 558000, burnRisk: "medium" },
];

const createInvoices = () => {
  const customers = ["Apex Build", "Lumen Services", "Koala Electrical", "Redfield Group"] as const;
  return Array.from({ length: 32 }, (_, index) => {
    const isOverdue = index % 3 === 0;
    const amount = 4600 + index * 1350;
    const daysOverdue = isOverdue ? 6 + (index % 5) * 8 : 0;

    return {
      id: `inv-${index + 1}`,
      customer: customers[index % 4] ?? customers[0],
      amount,
      dueDate: `2026-0${(index % 2) + 1}-${(index % 27) + 1}`,
      status: isOverdue ? "overdue" : index % 7 === 0 ? "paid" : "open",
      daysOverdue,
    } as const;
  });
};

export const createDemoSeed = (): DemoState => {
  return {
    currentUserId: "user-1",
    activeTheme: "obsidian",
    users: [
      { id: "user-1", name: "Jordan Clarke", email: "demo@quantira.app", role: "user", status: "active", createdAt: "2025-01-03T00:00:00.000Z" },
      { id: "user-2", name: "Nadia Singh", email: "nadia@quantira.app", role: "team", status: "active", createdAt: "2025-05-18T00:00:00.000Z" },
      { id: "user-3", name: "Priya Nolan", email: "admin@quantira.app", role: "admin", status: "active", createdAt: "2024-11-11T00:00:00.000Z" },
      { id: "user-4", name: "Leo Morris", email: "leo@quantira.app", role: "team", status: "active", createdAt: "2025-09-07T00:00:00.000Z" },
      { id: "user-5", name: "Ari Patel", email: "ari@quantira.app", role: "team", status: "active", createdAt: "2025-10-22T00:00:00.000Z" },
    ],
    groups: [
      {
        id: "group-vic-division",
        name: "VIC Division",
        description: "Consolidated view across Victoria entities",
        createdAt: "2026-01-12T03:40:00.000Z",
      },
    ],
    connections: [
      {
        id: "conn-xero-vic",
        platform: "xero",
        name: "Acme VIC - Xero",
        tenantId: "xero-48290",
        status: "healthy",
        groupIds: ["group-vic-division"],
        lastSyncAt: "2026-02-15T08:10:00.000Z",
      },
      {
        id: "conn-procore-vic",
        platform: "procore",
        name: "Fitouts VIC - Procore",
        tenantId: "pro-98310",
        status: "warning",
        groupIds: ["group-vic-division"],
        lastSyncAt: "2026-02-13T06:45:00.000Z",
      },
      {
        id: "conn-xero-nsw",
        platform: "xero",
        name: "Acme NSW - Xero",
        tenantId: "xero-18264",
        status: "healthy",
        groupIds: [],
        lastSyncAt: "2026-02-14T05:20:00.000Z",
      },
      {
        id: "conn-procore-nsw",
        platform: "procore",
        name: "Warehouse Ops - Procore",
        tenantId: "pro-45110",
        status: "healthy",
        groupIds: [],
        lastSyncAt: "2026-02-14T04:10:00.000Z",
      },
    ],
    monthlyFinancials: monthSeed,
    invoices: createInvoices(),
    projects,
    teamInvites: [
      {
        id: "invite-1",
        email: "finance.manager@acme.com",
        sentAt: "2026-02-12T08:00:00.000Z",
        expiresAt: "2026-02-19T08:00:00.000Z",
        permissions: {
          "conn-xero-vic": ["view", "edit"],
          "conn-procore-vic": ["view"],
        },
      },
    ],
    favorites: [],
    scheduledReports: [
      {
        id: "report-1",
        name: "Weekly Margin Pulse",
        query: "Where are we losing margin this month?",
        scheduleText: "Every Monday at 9am",
        scheduleRule: "RRULE:FREQ=WEEKLY;BYDAY=MO;BYHOUR=9",
        scope: "group-vic-division",
        recipients: ["owner@acme.com", "finance@acme.com"],
        subject: "Weekly Margin Pulse - Quantira",
        active: true,
        createdAt: "2026-02-10T04:12:00.000Z",
        nextRunAt: "2026-02-16T09:00:00.000Z",
      },
    ],
    chatThreads: [
      {
        id: "thread-1",
        title: "Margin pressure check",
        scope: "group-vic-division",
        createdAt: "2026-02-14T09:00:00.000Z",
        updatedAt: "2026-02-14T09:02:00.000Z",
        messages: [],
      },
    ],
    auditEvents: [
      {
        id: "audit-1",
        username: "Jordan Clarke",
        prompt: "Where are we losing margin this month?",
        responseSnippet: "Material variance and overtime are driving margin compression.",
        ipAddress: "203.14.67.211",
        occurredAt: "2026-02-14T09:02:00.000Z",
      },
      {
        id: "audit-2",
        username: "Nadia Singh",
        prompt: "Which projects are trending over budget?",
        responseSnippet: "Riverpoint Tower and Northline Civil are above budget trajectory.",
        ipAddress: "203.14.67.219",
        occurredAt: "2026-02-14T14:20:00.000Z",
      },
    ],
    waitlistEntries: [
      { id: "wait-1", name: "Noah Fields", email: "noah@ventures.com", phone: "+61 400 111 219", createdAt: "2026-02-09T10:05:00.000Z" },
      { id: "wait-2", name: "Lila Barnes", email: "lila@stonegroup.com", phone: "+61 420 226 771", createdAt: "2026-02-11T07:14:00.000Z" },
      { id: "wait-3", name: "Rowan Zed", email: "rowan@northhub.io", phone: "+61 401 608 912", createdAt: "2026-02-12T16:43:00.000Z" },
    ],
    pendingPrompt: "",
    selectedRange: "this-month",
  };
};

export const DEMO_NOW = demoNow;
